import { useAuthStore } from '@app/shared/store/auth.store';
import { useImpersonationStore } from '@app/shared/store/impersonation.store';
import { coordinatedRefresh } from '@app/shared/utils/auth-refresh-coordinator';

/** How many milliseconds before expiry to trigger a proactive refresh */
const MAX_REFRESH_MARGIN_MS = 5 * 60 * 1000; // 5 minutes

/** Minimum time before expiry to try refresh (avoids tight loops) */
const MIN_REFRESH_MARGIN_MS = 5 * 1000; // 5 seconds

let timerId: ReturnType<typeof setTimeout> | null = null;

/**
 * Decode the payload of a JWT without verifying the signature.
 * Returns null if the token is malformed.
 */
function decodeJwtExp(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;

    // JWT uses base64url encoding; normalize to base64 for atob
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      '='
    );

    const decoded = JSON.parse(atob(padded));
    return typeof decoded.exp === 'number' ? decoded.exp : null;
  } catch {
    return null;
  }
}

function computeRefreshDelayMs(expiresAtMs: number): number {
  const now = Date.now();
  const ttlMs = expiresAtMs - now;
  if (ttlMs <= 0) return 0;

  // Adaptive margin:
  // - For normal TTLs, refresh up to 5 minutes early
  // - For short TTLs (e.g., 1 minute), refresh ~10% before expiry (min 5s)
  const adaptiveMarginMs = Math.min(
    MAX_REFRESH_MARGIN_MS,
    Math.max(MIN_REFRESH_MARGIN_MS, Math.floor(ttlMs * 0.1))
  );

  // Schedule refresh at (expiry - margin)
  return Math.max(ttlMs - adaptiveMarginMs, 0);
}

/**
 * Schedule a silent token refresh a few minutes before the current
 * access token expires. If the token has already expired (or will
 * expire within the margin), the refresh fires immediately.
 */
export function scheduleTokenRefresh(accessToken: string): void {
  clearTokenRefresh();

  const exp = decodeJwtExp(accessToken);
  if (!exp) return;

  const expiresAtMs = exp * 1000;
  const delay = computeRefreshDelayMs(expiresAtMs);

  timerId = setTimeout(() => performSilentRefresh(), delay);
}

/**
 * Cancel any pending proactive refresh timer.
 */
export function clearTokenRefresh(): void {
  if (timerId !== null) {
    clearTimeout(timerId);
    timerId = null;
  }
}

/**
 * Attempt a silent refresh using the stored refresh token.
 * On success it updates the auth store and schedules the next refresh.
 * On failure it logs the user out.
 */
export async function performSilentRefresh(): Promise<void> {
  // An offline session has no live `/auth/refresh` to call — skip the proactive
  // refresh entirely rather than letting it fail into a logout. (The 401
  // interceptor never hits this for an offline session: a 401 means the request
  // reached the server, i.e. we are online.)
  if (useAuthStore.getState().isOfflineSession) return;

  // Route through the cross-tab coordinator: at most one `/auth/refresh` per
  // refresh-token generation across all tabs. `setSession` + timer re-arm are
  // handled inside the coordinator (rotated) or via adoption (adopted).
  const outcome = await coordinatedRefresh();
  if (outcome === 'rotated' || outcome === 'adopted') return;

  // `'failed'` = genuine failure (invalid/expired/revoked token, or none).
  // During impersonation fall back to sadmin's own token instead of wiping
  // the session; otherwise log out. Unchanged behavior.
  if (tryRestoreImpersonator()) return;
  useAuthStore.getState().logout();
}

/**
 * If mid-impersonation, put sadmin's tokens back on the auth store
 * and clear the impersonation slice. Returns true when the restore
 * happened so callers can skip the destructive logout path. Uses
 * the batched `setSession` action so token / refreshToken / user
 * always change together — no half-swapped frame. See
 * "Batch coupled React state" in apps/neocrm/CLAUDE.md.
 */
function tryRestoreImpersonator(): boolean {
  const imp = useImpersonationStore.getState();
  if (
    !imp.isImpersonating ||
    !imp.originalToken ||
    !imp.originalRefreshToken ||
    !imp.originalUser
  ) {
    return false;
  }
  useAuthStore.getState().setSession({
    token: imp.originalToken,
    refreshToken: imp.originalRefreshToken,
    user: imp.originalUser,
  });
  imp.endImpersonation();
  scheduleTokenRefresh(imp.originalToken);
  return true;
}
